import { useState, useCallback, ChangeEvent, FormEvent } from 'react';

// Types for validation
type ValidationRule<T> = (value: T[keyof T]) => string | undefined;
type ValidationRules<T> = Partial<Record<keyof T, ValidationRule<T>[]>>;

// Interface for the useForm hook return value
interface UseFormReturn<T> {
  values: T;
  errors: Partial<Record<keyof T, string>>;
  touched: Partial<Record<keyof T, boolean>>;
  isValid: boolean;
  isDirty: boolean;
  handleChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
  handleBlur: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
  handleSubmit: (onSubmit: (values: T) => void) => (e: FormEvent) => void;
  setFieldValue: (name: keyof T, value: any) => void;
  setFieldTouched: (name: keyof T, isTouched: boolean) => void;
  resetForm: () => void;
  validateForm: () => boolean;
}

/**
 * Custom hook for form state management and validation
 * 
 * @param initialValues - Initial form values
 * @param validationRules - Validation rules for form fields
 * @returns Object containing form state and handlers
 */
function useForm<T extends Record<string, any>>(
  initialValues: T,
  validationRules: ValidationRules<T> = {}
): UseFormReturn<T> {
  const [values, setValues] = useState<T>(initialValues);
  const [errors, setErrors] = useState<Partial<Record<keyof T, string>>>({});
  const [touched, setTouched] = useState<Partial<Record<keyof T, boolean>>>({});
  const [isDirty, setIsDirty] = useState<boolean>(false);

  // Validate a single field
  const validateField = useCallback(
    (name: keyof T, value: any): string | undefined => {
      const fieldRules = validationRules[name];
      if (!fieldRules) return undefined;

      for (const rule of fieldRules) {
        const error = rule(value);
        if (error) return error;
      }

      return undefined;
    },
    [validationRules]
  );

  // Validate all fields
  const validateForm = useCallback((): boolean => {
    const newErrors: Partial<Record<keyof T, string>> = {};
    let isValid = true;

    // Validate each field with rules
    Object.keys(validationRules).forEach((key) => {
      const fieldName = key as keyof T;
      const error = validateField(fieldName, values[fieldName]);
      
      if (error) {
        newErrors[fieldName] = error;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  }, [validateField, validationRules, values]);

  // Handle input change
  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      const { name, value, type } = e.target;
      
      // Handle different input types
      let parsedValue: any = value;
      if (type === 'number') {
        parsedValue = value === '' ? '' : Number(value);
      } else if (type === 'checkbox') {
        parsedValue = (e.target as HTMLInputElement).checked;
      }
      
      setValues((prev) => ({ ...prev, [name]: parsedValue }));
      setIsDirty(true);
      
      // Validate field if it's been touched
      if (touched[name as keyof T]) {
        const error = validateField(name as keyof T, parsedValue);
        setErrors((prev) => ({ ...prev, [name]: error }));
      }
    },
    [touched, validateField]
  );

  // Handle input blur
  const handleBlur = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      const { name } = e.target;
      
      setTouched((prev) => ({ ...prev, [name]: true }));
      
      // Validate field on blur
      const error = validateField(name as keyof T, values[name as keyof T]);
      setErrors((prev) => ({ ...prev, [name]: error }));
    },
    [validateField, values]
  );

  // Handle form submission
  const handleSubmit = useCallback(
    (onSubmit: (values: T) => void) => (e: FormEvent) => {
      e.preventDefault();
      
      // Mark all fields as touched
      const allTouched = Object.keys(values).reduce(
        (acc, key) => ({ ...acc, [key]: true }),
        {} as Record<keyof T, boolean>
      );
      
      setTouched(allTouched);
      
      // Validate all fields
      const isValid = validateForm();
      
      // Submit if valid
      if (isValid) {
        onSubmit(values);
      }
    },
    [validateForm, values]
  );

  // Set a field value programmatically
  const setFieldValue = useCallback((name: keyof T, value: any) => {
    setValues((prev) => ({ ...prev, [name]: value }));
    setIsDirty(true);
    
    // Validate field if it's been touched
    if (touched[name]) {
      const error = validateField(name, value);
      setErrors((prev) => ({ ...prev, [name]: error }));
    }
  }, [touched, validateField]);

  // Set a field touched state programmatically
  const setFieldTouched = useCallback((name: keyof T, isTouched: boolean) => {
    setTouched((prev) => ({ ...prev, [name]: isTouched }));
    
    // Validate field if being marked as touched
    if (isTouched) {
      const error = validateField(name, values[name]);
      setErrors((prev) => ({ ...prev, [name]: error }));
    }
  }, [validateField, values]);

  // Reset form to initial state
  const resetForm = useCallback(() => {
    setValues(initialValues);
    setErrors({});
    setTouched({});
    setIsDirty(false);
  }, [initialValues]);

  // Check if form is valid
  const isValid = Object.keys(errors).length === 0;

  return {
    values,
    errors,
    touched,
    isValid,
    isDirty,
    handleChange,
    handleBlur,
    handleSubmit,
    setFieldValue,
    setFieldTouched,
    resetForm,
    validateForm,
  };
}

// Common validation rules
export const required = (message = 'This field is required') => 
  (value: any) => {
    if (value === undefined || value === null || value === '') {
      return message;
    }
    return undefined;
  };

export const minLength = (min: number, message = `Must be at least ${min} characters`) => 
  (value: string) => {
    if (!value || value.length < min) {
      return message;
    }
    return undefined;
  };

export const maxLength = (max: number, message = `Must be at most ${max} characters`) => 
  (value: string) => {
    if (value && value.length > max) {
      return message;
    }
    return undefined;
  };

export const email = (message = 'Invalid email address') => 
  (value: string) => {
    if (!value) return undefined;
    
    const regex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    if (!regex.test(value)) {
      return message;
    }
    return undefined;
  };

export const pattern = (regex: RegExp, message = 'Invalid format') => 
  (value: string) => {
    if (!value) return undefined;
    
    if (!regex.test(value)) {
      return message;
    }
    return undefined;
  };

export const number = (message = 'Must be a number') => 
  (value: any) => {
    if (value === undefined || value === null || value === '') return undefined;
    
    if (isNaN(Number(value))) {
      return message;
    }
    return undefined;
  };

export const min = (minValue: number, message = `Must be at least ${minValue}`) => 
  (value: any) => {
    if (value === undefined || value === null || value === '') return undefined;
    
    if (Number(value) < minValue) {
      return message;
    }
    return undefined;
  };

export const max = (maxValue: number, message = `Must be at most ${maxValue}`) => 
  (value: any) => {
    if (value === undefined || value === null || value === '') return undefined;
    
    if (Number(value) > maxValue) {
      return message;
    }
    return undefined;
  };

export default useForm;
